import React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import {
    useCreateMembershipGroup,
    type CreateMembershipGroupFormSchemaValues,
    createMembershipGroupFormSchema,
} from 'src/features/memberships/api/useCreateMembershipGroup';
import { useGetMembershipGroup } from 'src/features/memberships/api/useGetMembershipGroup';
import { useUpdateMembershipGroup } from 'src/features/memberships/api/useUpdateMembershipGroup';
import { MembershipGroupForm } from 'src/features/memberships/components/forms/MembershipGroupForm';
import { ALLOWED_DAYS, type MembershipGroup } from 'src/features/memberships/types';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { tableMessages } from 'src/registry/components/table/utils/messages';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { DataTableNewEntryProps } from 'src/registry/components/table/types';

interface MembershipGroupFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembershipGroup?: MembershipGroup;
    modalMode?: 'edit' | 'copy' | 'create';
    children?: React.ReactNode;
}

function MembershipGroupFormModal<TData>({
    selectedMembershipGroup,
    className,
    semantics,
    modalMode,
    children,
    onSubmit: updateTable,
}: MembershipGroupFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId, parkingRole } = useParking();
    const navigate = useNavigate();

    const { data: membershipGroup } = useGetMembershipGroup({
        parkingId: parkingId,
        membershipGroupId: selectedMembershipGroup?.id ?? -1,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedMembershipGroup?.id },
    });

    const { mutate: createMembershipGroup, isPending: isPendingCreate } = useCreateMembershipGroup({
        onSuccess: ({ name, id }) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(membershipMessages.createMembershipGroupSuccess),
                description: formatMessage(membershipMessages.createMembershipGroupSuccessDescription, {
                    membershipGroupName: name,
                }),
            });
            void navigate(`/vehicles/memberships/${id}`);
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: updateMembershipGroup, isPending: isPendingUpdate } = useUpdateMembershipGroup({
        onSuccess: ({ name }) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(membershipMessages.updateMembershipGroupSuccess),
                description: formatMessage(membershipMessages.updateMembershipGroupSuccessDescription, {
                    membershipGroupName: name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const {
        name = '',
        valid_from = '',
        valid_until = '',
        allowed_hours_start = '00:00:00',
        allowed_hours_end = '23:59:59',
        allowed_points = [],
        allowed_days = Object.values(ALLOWED_DAYS),
        allowed_parking_places = 1,
        allowed_parkings = [],
        reserve_place = false,
        description = undefined,
        points_groups = [],
    } = membershipGroup || {};

    const isEdit = !!(selectedMembershipGroup?.id && modalMode === 'edit');

    const buttonTile = formatMessage(
        isEdit
            ? membershipMessages.editMembership
            : modalMode === 'copy'
              ? membershipMessages.copyMembership
              : membershipMessages.addMembership,
    );

    const modalIcon = isEdit ? ICON_MAP.EDIT : modalMode === 'copy' ? ICON_MAP.COPY : ICON_MAP.ADD;

    const modalDescription = isEdit
        ? formatMessage(membershipMessages.editMembershipSubtitle, {
              entry: selectedMembershipGroup?.name,
          })
        : modalMode === 'copy'
          ? formatMessage(membershipMessages.copyMembershipDescription)
          : formatMessage(membershipMessages.addMembership);

    const form = useForm<CreateMembershipGroupFormSchemaValues>({
        resolver: zodResolver(createMembershipGroupFormSchema),
        defaultValues: {
            name,
            valid_range: {
                from: valid_from ? new Date(valid_from) : undefined,
                to: valid_until ? new Date(valid_until) : undefined,
            },
            allowed_hours_start,
            allowed_hours_end,
            allowed_points,
            allowed_days,
            allowed_parking_places,
            allowed_parkings,
            reserve_place,
            description,
            allowed_group_points: points_groups,
        },
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset({
            name,
            valid_range: {
                from: valid_from ? new Date(valid_from) : undefined,
                to: valid_until ? new Date(valid_until) : undefined,
            },
            allowed_hours_start,
            allowed_hours_end,
            allowed_points,
            allowed_days,
            allowed_parking_places,
            allowed_parkings,
            reserve_place,
            description,
            allowed_group_points: points_groups,
        });
    }, [membershipGroup]);

    const onSubmit = ({ valid_range, ...values }: CreateMembershipGroupFormSchemaValues) => {
        // we need to take only the date, regardless of timezones:
        const submitData = {
            parkingId: parkingId,
            ...values,
            valid_from: `${valid_range.from?.getFullYear()}-${valid_range.from?.getMonth() + 1}-${valid_range.from?.getDate()}T00:00:00`,
            valid_until: `${valid_range.to?.getFullYear()}-${valid_range.to?.getMonth() + 1}-${valid_range.to?.getDate()}T23:59:59`,
        };
        if (isEdit) updateMembershipGroup({ membershipGroupId: selectedMembershipGroup.id, ...submitData });
        else createMembershipGroup(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        if (setIsModalOpen) {
            setIsModalOpen(isOpen);
        }
    };

    if (parkingRole == MEMBERSHIP_ROLES.CLIENT) return <></>;

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {!children && !selectedMembershipGroup && !isModalOpen && (
                <DialogTrigger asChild>
                    <Button variant="outline" className={cn('ml-auto', className)}>
                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                        {formatMessage(tableMessages.new, {
                            entry: semantics?.entryName?.toLowerCase(),
                        })}
                    </Button>
                </DialogTrigger>
            )}
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={modalIcon} />
                        <div>
                            <DialogTitle>{buttonTile}</DialogTitle>
                            <DialogDescription>{modalDescription}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <MembershipGroupForm form={form} onSubmit={onSubmit} modalMode={modalMode} />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button
                        disabled={
                            isPendingCreate || isPendingUpdate || (selectedMembershipGroup && !form.formState.isDirty)
                        }
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPendingCreate || isPendingUpdate ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {buttonTile}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { MembershipGroupFormModal };
